import {
  AutotractorPropsType,
  AutotractorSlotIDs,
  Domain,
  InteractionLayer,
  LayerConfig,
  MapImageId,
} from "./types";
import { CenterPivot } from "common/components/map/CenterPivot";
import { Expression } from "mapbox-gl";
import {
  FarmMapData,
  filterValidForBuffer,
  getPointFeature,
  getZoneFeature,
  PointFeature,
  ZoneFeature,
  ZoneFeatureProperties,
} from "common/utils/farm";
import { featureCollection } from "@turf/helpers";
import { FeatureCollection, Geometry } from "geojson";
import { FixType } from "protos/geo/geo";
import { Layer, Source } from "react-map-gl";
import { simpleGeometries } from "common/utils/geo";
import { SymbolLayer } from "./SymbolLayer";
import { resolvedTheme as theme } from "common/theme/tailwind.theme";
import buffer from "@turf/buffer";
import mask from "@turf/mask";
import React, { FC, useMemo } from "react";

// Fixed width used to render paths so they're clearly visible
const PATH_DISPLAY_WIDTH_M = 4;

enum LayerId {
  FocusedZone = "focused-zone",
  FocusedZoneOutline = "farm-area-focused-zone-outline",
  SelectedZone = "selected-zone",
  SelectedZoneOutline = "farm-area-selected-zone-outline",
  InteractableZonesOutline = "interactable-zones-outline",
  InnerHeadlands = "farm-inner-headlands",
  InteractableZones = "interactable-zones",
  NonInteractableZones = "non-interactable-zones",
  ObstacleBuffers = "obstacle-buffers",
  ObstacleBuffersOutline = "obstacle-buffers-outline",
  SelectedPath = "farm-selected-path",
  PathSwaths = "farm-path-swaths",
  PathSwathsOutline = "farm-path-swaths-outline",
  Paths = "farm-paths",
  PathArrows = "farm-paths-arrows",
  PathHitTargets = "farm-paths-hit-targets",
  FocusedPoint = "farm-focused-point",
  SelectedPoint = "farm-selected-point",
  SelectedPointOutline = "farm-selected-point-outline",
  Points = "points",
}

export const useFarmLayers = ({
  clickPoint,
  hoverPoint,
  clickZone,
  hoverZone,
  leaveZone,
}: {
  clickPoint?: (feature?: PointFeature) => void;
  hoverPoint?: (feature?: PointFeature) => void;
  clickZone?: (feature?: ZoneFeature) => void;
  hoverZone?: (feature?: ZoneFeature) => void;
  leaveZone?: () => void;
}): LayerConfig<FarmLayersProps> => {
  const pointInteractionLayers: InteractionLayer[] = [
    {
      id: LayerId.Points,
      cursor: "pointer",
      onClick: (e) => clickPoint?.(getPointFeature(e)),
      onMouseMove: (e) => hoverPoint?.(getPointFeature(e)),
    },
  ];
  const zoneInteractionLayers: InteractionLayer[] = [
    {
      id: LayerId.InteractableZones,
      cursor: "pointer",
      onClick: (e) => clickZone?.(getZoneFeature(e)),
      onMouseMove: (e) => hoverZone?.(getZoneFeature(e)),
      onLeave: leaveZone,
    },
    {
      id: LayerId.PathHitTargets,
      cursor: "pointer",
      onClick: (e) => clickZone?.(getZoneFeature(e)),
      onMouseMove: (e) => hoverZone?.(getZoneFeature(e)),
      onLeave: leaveZone,
    },
  ];
  // a layer nobody listens to shouldn't change the cursor either
  const interactionLayers = [
    ...(clickPoint || hoverPoint ? pointInteractionLayers : []),
    ...(clickZone || hoverZone ? zoneInteractionLayers : []),
  ];

  return {
    interactionLayers,
    idsToLoad: [MapImageId.GpsPoint, MapImageId.RtkFixed, MapImageId.RtkFloat],
    Layers: FarmLayers,
  };
};

interface FarmLayersProps {
  farmsMapData: FarmMapData[];
  hidePivots?: boolean;
  shownPoints: string[];
  interactableZoneIds?: string[];
  focusedZoneIds: string[];
  selectedZoneIds: string[];
  focusedPointId?: string;
  selectedPointId?: string;
  beforeId?: string;
  innerHeadlandHintWidthM?: number;
  slotIds?: string[];
}

const _FarmLayers: FC<FarmLayersProps> = ({
  farmsMapData,
  focusedZoneIds,
  selectedZoneIds,
  shownPoints,
  interactableZoneIds,
  hidePivots,
  focusedPointId,
  selectedPointId,
  beforeId,
  innerHeadlandHintWidthM,
  slotIds,
}) => {
  const points = useMemo<FeatureCollection>(() => {
    return {
      type: "FeatureCollection",
      features: farmsMapData.flatMap((f) =>
        Object.values(f.pointsById).map((p) => ({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [p.lng, p.lat],
          },
          properties: {
            ...p,
            pointId: p.id?.id,
            type: AutotractorPropsType.POINT,
            domain: Domain.AUTOTRACTOR,
            __portalFeatureProps: true,
          },
        }))
      ),
    };
  }, [farmsMapData]);

  // select the correct icon image name from the point's fix type
  const pointIconByFixType: Expression = [
    "case",
    ["==", ["get", "fixType", ["get", "captureInfo"]], FixType.RTK_FIXED],
    MapImageId.RtkFixed,
    ["==", ["get", "fixType", ["get", "captureInfo"]], FixType.RTK_FLOAT],
    MapImageId.RtkFloat,
    MapImageId.GpsPoint,
  ];

  const centerPivots = useMemo(
    () =>
      farmsMapData.flatMap((fmd) => {
        return Object.values(fmd.zonesById)
          .filter((z) => z.contents?.centerPivot)
          .map((z) => {
            if (z.contents?.centerPivot?.center?.id?.id) {
              return {
                ...z,
                contents: {
                  centerPivot: {
                    ...z.contents.centerPivot,
                    center: fmd.pointsById[z.contents.centerPivot.center.id.id],
                  },
                },
              };
            }
            return z;
          });
      }),
    [farmsMapData]
  );

  const focusedZoneIdsSet = new Set(focusedZoneIds);
  const selectedZoneIdsSet = new Set(selectedZoneIds);
  const allZoneIdsSet = new Set(
    farmsMapData.flatMap((fmd) => Object.keys(fmd.zonesById))
  );
  let interactableZoneIdsSet: Set<string>;
  let nonInteractableZoneIdsSet: Set<string>;

  const focusedPointFilter = ["==", ["get", "pointId"], focusedPointId ?? ""];
  const selectedPointFilter = ["==", ["get", "pointId"], selectedPointId ?? ""];

  if (interactableZoneIds) {
    // only zones listed in interactableZoneIds are interactable
    interactableZoneIdsSet = new Set(interactableZoneIds);
    nonInteractableZoneIdsSet = allZoneIdsSet.difference(
      interactableZoneIdsSet
    );
  } else {
    // default to every zone being interactable
    interactableZoneIdsSet = allZoneIdsSet;
    nonInteractableZoneIdsSet = new Set();
  }

  // build subsets of zone geometry
  const allZoneFeatures = farmsMapData.flatMap(
    (fmd) => fmd.areasGeojson.features
  );
  const focusedZones = featureCollection(
    allZoneFeatures.filter(
      (f) => f.properties.zoneId && focusedZoneIdsSet.has(f.properties.zoneId)
    )
  );
  const selectedZones = featureCollection(
    allZoneFeatures.filter(
      (f) => f.properties.zoneId && selectedZoneIdsSet.has(f.properties.zoneId)
    )
  );
  const interactableZones = featureCollection(
    allZoneFeatures.filter(
      (f) =>
        f.properties.zoneId && interactableZoneIdsSet.has(f.properties.zoneId)
    )
  );
  const nonInteractableZones = featureCollection(
    allZoneFeatures.filter(
      (f) =>
        f.properties.zoneId &&
        nonInteractableZoneIdsSet.has(f.properties.zoneId)
    )
  );

  const paths = featureCollection<Geometry, ZoneFeatureProperties>(
    farmsMapData.flatMap((fmd) => fmd.paths.features)
  );
  // paths introduce a lot of visual clutter, so only show them when
  // hovered or selected.
  const visiblePathIds = [
    ...new Set([...focusedZoneIdsSet, ...selectedZoneIdsSet]),
  ];
  const visiblePathFilter = [
    "in",
    ["get", "zoneId"],
    ["literal", visiblePathIds],
  ];
  const selectedPathFilter = [
    "in",
    ["get", "zoneId"],
    ["literal", [...selectedZoneIdsSet]],
  ];
  // only a drawn path can be clicked, and only where this map allows it
  const interactablePathFilter = [
    "in",
    ["get", "zoneId"],
    [
      "literal",
      visiblePathIds.filter((zoneId) => interactableZoneIdsSet.has(zoneId)),
    ],
  ];

  const focusedMarkerFilter = [
    "==",
    ["get", "id", ["get", "id"]],
    focusedPointId ?? "",
  ];
  const selectedMarkerFilter = [
    "==",
    ["get", "id", ["get", "id"]],
    selectedPointId ?? "",
  ];
  const shownPointsFilter = [
    "in",
    ["get", "id", ["get", "id"]],
    ["literal", shownPoints],
  ];
  const focusedOrSelectedOrShownFilter = [
    "any",
    selectedMarkerFilter,
    focusedMarkerFilter,
    shownPointsFilter,
  ];

  const innerHeadlands = useMemo<
    FeatureCollection<Geometry, ZoneFeatureProperties> | undefined
  >(() => {
    if (!innerHeadlandHintWidthM) {
      return;
    }
    // calling turf/buffer on non-closed fields throws an error and causes the map to crash.
    // non-closed fields WILL exist, and we need to handle them.
    const validFields = farmsMapData
      .flatMap((fmd) => fmd.fields.features)
      .filter((f) => filterValidForBuffer(f));

    return {
      type: "FeatureCollection",
      // this nonsense is required because @turf/buffer has two very "helpful"
      // properties:
      //
      // - when you pass it most Feature<T extends Geometry>, it'll return
      //   Feature<Geometry>; however, when you pass it
      //   Feature<GeometryCollection<T>> it returns FeatureCollection<T>.
      // - the type definitions in 6.5.0 are at least somewhat broken and have a
      //   fallthrough overload which declares that buffer will turn a
      //   Feature<any> into a Feature<Polygon | MultiPolygon>, and it is
      //   obnoxious to convince Typescript that this isn't the overload it
      //   should be using.
      //
      // in practice, all of our Features are Feature<GeometryCollection>, and
      // so the easiest way of getting this to work reliably is to break apart
      // GeometryCollections into their simple Geometry constituents.
      features: validFields.flatMap((ft) => {
        return simpleGeometries(ft.geometry).map((geom) => {
          const bufferedGeom = buffer(geom, -innerHeadlandHintWidthM, {
            units: "meters",
          });

          return {
            ...bufferedGeom,
            properties: ft.properties,
          };
        });
      }),
    };
  }, [farmsMapData, innerHeadlandHintWidthM]);

  const obstacleBuffers = useMemo<
    FeatureCollection<Geometry, ZoneFeatureProperties> | undefined
  >(() => {
    // calling turf/buffer on non-closed fields throws an error and causes the map to crash.
    // non-closed fields WILL exist, and we need to handle them.
    const validObstacles = farmsMapData
      .flatMap((fmd) => fmd.obstacles.features)
      .filter((f) => filterValidForBuffer(f));

    return {
      type: "FeatureCollection",
      // this nonsense is required because @turf/buffer has two very "helpful"
      // properties:
      //
      // - when you pass it most Feature<T extends Geometry>, it'll return
      //   Feature<Geometry>; however, when you pass it
      //   Feature<GeometryCollection<T>> it returns FeatureCollection<T>.
      // - the type definitions in 6.5.0 are at least somewhat broken and have a
      //   fallthrough overload which declares that buffer will turn a
      //   Feature<any> into a Feature<Polygon | MultiPolygon>, and it is
      //   obnoxious to convince Typescript that this isn't the overload it
      //   should be using.
      //
      // in practice, all of our Features are Feature<GeometryCollection>, and
      // so the easiest way of getting this to work reliably is to break apart
      // GeometryCollections into their simple Geometry constituents.
      features: validObstacles.flatMap((ft) => {
        return simpleGeometries(ft.geometry).map((geom) => {
          const bufferedGeom = buffer(geom, ft.properties.bufferM ?? 0, {
            units: "meters",
          });

          if (
            geom.type === "Polygon" &&
            bufferedGeom.geometry.type === "Polygon"
          ) {
            const masked = mask(geom, bufferedGeom.geometry);
            return { ...masked, properties: ft.properties };
          }

          return {
            ...bufferedGeom,
            properties: ft.properties,
          };
        });
      }),
    };
  }, [farmsMapData]);

  // paths are drawn with a buffered swath around the recorded line,
  // plus a dashed centerline and direction arrows.
  const pathSwaths = useMemo<
    FeatureCollection<Geometry, ZoneFeatureProperties>
  >(() => {
    const validPaths = farmsMapData
      .flatMap((fmd) => fmd.paths.features)
      .filter((f) => filterValidForBuffer(f));

    return {
      type: "FeatureCollection",
      features: validPaths.flatMap((ft) => {
        return simpleGeometries(ft.geometry).map((geom) => {
          const bufferedGeom = buffer(geom, PATH_DISPLAY_WIDTH_M / 2, {
            units: "meters",
            steps: 2,
          });

          return {
            ...bufferedGeom,
            properties: ft.properties,
          };
        });
      }),
    };
  }, [farmsMapData]);

  return (
    <>
      {innerHeadlands && (
        <Source
          key="inner-headlands"
          id="inner-headlands"
          type="geojson"
          data={innerHeadlands}
        >
          <Layer
            id={LayerId.InnerHeadlands}
            beforeId={beforeId ?? undefined}
            type="line"
            paint={{
              "line-color": "black",
              "line-width": 2,
              "line-dasharray": [2, 2],
            }}
            filter={["boolean", true]}
          />
        </Source>
      )}
      <Source
        key="farm-focused-areas"
        id="farm-focused-areas"
        type="geojson"
        data={focusedZones}
      >
        <Layer
          id={LayerId.FocusedZone}
          beforeId={LayerId.FocusedZoneOutline}
          type="fill"
          // ramp opacity up as the user zooms out to counteract the decreasing
          // size of the zone on their screen
          paint={{
            "fill-color": theme.colors.carbon.map.farms.focused,
            "fill-opacity": [
              "interpolate",
              ["linear"],
              ["zoom"],
              8,
              0.6,
              16,
              0.1,
            ],
          }}
        />
        <Layer
          id={LayerId.FocusedZoneOutline}
          beforeId={LayerId.SelectedZone}
          type={"line"}
          paint={{
            "line-color": theme.colors.carbon.map.farms.default,
            // make halo around zone thicker as the user zooms in, up to a point
            "line-blur": ["interpolate", ["linear"], ["zoom"], 8, 5, 16, 10],
            "line-width": ["interpolate", ["linear"], ["zoom"], 8, 10, 16, 20],
            "line-opacity": 0.7,
          }}
        />
      </Source>
      <Source
        key="farm-selected-areas"
        id="farm-selected-areas"
        type="geojson"
        data={selectedZones}
      >
        <Layer
          id={LayerId.SelectedZone}
          beforeId={LayerId.SelectedZoneOutline}
          type="fill"
          // ramp opacity up as the user zooms out to counteract the decreasing
          // size of the zone on their screen
          paint={{
            "fill-color": theme.colors.carbon.map.farms.focused,
            "fill-opacity": [
              "interpolate",
              ["linear"],
              ["zoom"],
              8,
              0.7,
              16,
              0.3,
            ],
          }}
        />
        <Layer
          id={LayerId.SelectedZoneOutline}
          beforeId={beforeId ?? undefined}
          type={"line"}
          paint={{
            "line-color": theme.colors.carbon.map.farms.focused,
            "line-width": 2,
          }}
        />
      </Source>
      <Source
        key="farm-interactable-areas"
        id="farm-interactable-areas"
        type="geojson"
        data={interactableZones}
      >
        <Layer
          id={LayerId.InteractableZonesOutline}
          beforeId={LayerId.FocusedZone}
          type="line"
          paint={{
            "line-color": theme.colors.carbon.map.farms.default,
            "line-width": 2,
          }}
        />
        <Layer
          id={LayerId.InteractableZones}
          beforeId={LayerId.InteractableZonesOutline}
          type={"fill"}
          paint={{
            "fill-color": ["get", "zoneColor"],
            "fill-opacity": 0.2,
          }}
        />
      </Source>
      <Source
        key="farm-noninteractable-areas"
        id="farm-noninteractable-areas"
        type="geojson"
        data={nonInteractableZones}
      >
        <Layer
          id={LayerId.NonInteractableZones}
          beforeId={LayerId.SelectedZoneOutline}
          type={"fill"}
          paint={{
            "fill-color": ["get", "zoneColor"],
            "fill-opacity": 0.2,
          }}
        />
      </Source>
      <Source
        key="farm-obstacle-buffers"
        id="farm-obstacle-buffers"
        type="geojson"
        data={obstacleBuffers}
      >
        <Layer
          id={LayerId.ObstacleBuffers}
          beforeId={
            slotIds?.includes(AutotractorSlotIDs.OBSTACLES)
              ? AutotractorSlotIDs.OBSTACLES
              : LayerId.SelectedZoneOutline
          }
          type={"fill"}
          paint={{
            "fill-color": "#FF0000",
            "fill-opacity": 0.2,
          }}
        />
        <Layer
          id={LayerId.ObstacleBuffersOutline}
          beforeId={LayerId.SelectedZoneOutline}
          type={"line"}
          paint={{
            "line-dasharray": [8, 8],
            "line-color": "#FF0000",
          }}
        />
      </Source>
      <Source
        key="farm-path-swaths"
        id="farm-path-swaths"
        type="geojson"
        data={pathSwaths}
      >
        <Layer
          id={LayerId.PathSwaths}
          beforeId={LayerId.SelectedZoneOutline}
          type="fill"
          paint={{
            "fill-color": [
              "coalesce",
              ["get", "zoneColor"],
              theme.colors.white,
            ],
            "fill-opacity": 0.2,
          }}
          filter={visiblePathFilter}
        />
        <Layer
          id={LayerId.SelectedPath}
          beforeId={LayerId.SelectedZoneOutline}
          type="fill"
          paint={{
            "fill-color": theme.colors.carbon.map.farms.focused,
            "fill-opacity": [
              "interpolate",
              ["linear"],
              ["zoom"],
              8,
              0.7,
              16,
              0.3,
            ],
          }}
          filter={selectedPathFilter}
        />
        <Layer
          id={LayerId.PathSwathsOutline}
          beforeId={LayerId.SelectedZoneOutline}
          type="line"
          paint={{
            "line-color": theme.colors.black,
            "line-width": 2,
          }}
          filter={visiblePathFilter}
        />
        <Layer
          id={LayerId.PathHitTargets}
          beforeId={LayerId.SelectedZoneOutline}
          type="fill"
          paint={{ "fill-opacity": 0 }}
          filter={interactablePathFilter}
        />
      </Source>
      <Source key="farm-paths" id="farm-paths" type="geojson" data={paths}>
        <Layer
          id={LayerId.Paths}
          beforeId={LayerId.SelectedZoneOutline}
          type="line"
          paint={{
            "line-color": theme.colors.black,
            "line-width": 3,
            // dash lengths are multiples of line-width
            "line-dasharray": [5, 2.5],
          }}
          filter={visiblePathFilter}
        />
        <Layer
          id={LayerId.PathArrows}
          beforeId={LayerId.SelectedZoneOutline}
          type="symbol"
          layout={{
            "symbol-placement": "line",
            "symbol-spacing": 100,
            "text-field": "▶",
            "text-size": 40,
            "text-keep-upright": false,
            "text-rotation-alignment": "map",
          }}
          paint={{
            "text-color": theme.colors.black,
          }}
          filter={visiblePathFilter}
        />
      </Source>
      {!hidePivots &&
        centerPivots.map((centerPivot, i) => (
          <CenterPivot key={`${i}-${centerPivot.id?.id}`} zone={centerPivot} />
        ))}
      <Source
        key="farm-points"
        id="farm-points"
        type="geojson"
        data={featureCollection(
          farmsMapData.flatMap((fmd) => fmd.pointsGeojson.features)
        )}
      >
        {focusedPointId && (
          <Layer
            beforeId={LayerId.Points}
            id={LayerId.FocusedPoint}
            type={"circle"}
            paint={{
              "circle-radius": 16,
              "circle-color": theme.colors.carbon.map.farms.focused,
              "circle-opacity": 0.75,
            }}
            filter={focusedPointFilter}
          />
        )}
        {selectedPointId && (
          <Layer
            id={LayerId.SelectedPointOutline}
            beforeId={LayerId.Points}
            type={"circle"}
            paint={{
              "circle-radius": 16,
              "circle-color": theme.colors.black,
              "circle-opacity": 1,
            }}
            filter={selectedPointFilter}
          />
        )}
        {selectedPointId && (
          <Layer
            id={LayerId.SelectedPoint}
            beforeId={LayerId.Points}
            type={"circle"}
            paint={{
              "circle-radius": 14,
              "circle-color": theme.colors.blue[600],
              "circle-opacity": 1,
            }}
            filter={selectedPointFilter}
          />
        )}
      </Source>
      <SymbolLayer
        id={LayerId.Points}
        beforeId={beforeId ?? undefined}
        iconName={pointIconByFixType}
        featureCollection={points}
        filter={focusedOrSelectedOrShownFilter}
      />
    </>
  );
};

export const FarmLayers = React.memo(_FarmLayers);
